import { Shinsei } from "@/types/Shinsei";
import { Flow } from "@/types/SyouninGroup";
import { ref, computed, Ref } from "vue";
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { ShinseiOption } from "@/types/Option";
import { AuthUser } from "@/types/User";
import { ShinseiService } from "@/service/ShinseiService";
import { MstSyouninGroupsService } from "@/service/MstSyouninGroupsService";
import { DialogService } from "@/service/DialogService";
import { RouterConst } from "@/const/Router";
import OptionConst from "@/const/Option";

/*
 * 初期処理
 */
export function useForm() {
  const loading = ref(0) // APIで読み込みをする本数
  const isError = ref(false)

  const router = useRouter()
  const flow = ref<Flow[]>([])
  const user = ref<AuthUser | null>(null)
  const options = ref<ShinseiOption | null>(null)

  const shinseiSrv = new ShinseiService()
  const syouninSrv = new MstSyouninGroupsService()

  const getApprover = async (shinseiType: number | null, isDialog: boolean = true) => {
    loading.value++
    const syouninType = OptionConst.SHINSEI_CODE_FOR_FLOW_TYPES.find(v => v.code == shinseiType)?.flow_type ?? OptionConst.SYOUNIN_FLOW_TYPES.DEFAULT
    syouninSrv.getApprover(syouninType).then(val => {
      if (isDialog && val.length < 1) {
        // エラー
        DialogService.warning({
          title: '承認グループがありません',
          text: '先に承認グループを登録してください。',
        }).then(() => {
          router.push({
            name: RouterConst.SHINSEI_TYPE_LIST
          })
        })
      }
      flow.value = val
    })
    loading.value--
  }

  // ユーザー情報の取得
  const getUser = async () => {
    loading.value++
    const authStore = useAuthStore()
    user.value = authStore.user
    loading.value--
  }

  // オプションの取得
  const getShinseiOptions = async () => {
    loading.value++
    await shinseiSrv.getShinseiOption().then(opt => {
      if (opt) {
        options.value = opt
      } else {
        isError.value = true
      }
    })
    loading.value--
  }

  const setShinseiType = (shinseiType: string): number | null => {
    return OptionConst.SHINSEI_CODE_TYPE.find(v => v.type == shinseiType)?.code ?? null
  }

  // 申請情報の取得
  const getShinseiInfo = async (form: Ref<Shinsei>, id: number) => {
    loading.value++
    const res = await shinseiSrv.getInfo(id)
    if(!res){
      DialogService.warning({
        title: '申請情報取得失敗',
        text: '申請が存在しないか、取下げられた可能性があります。',
      }).then(() => {
        router.push({
          name: RouterConst.SHINSEI_LIST
        })
      })
    }
    loading.value--
    return res
  }

  const getShinseiSyouninFlow = async (id: number, shinseiType: number, userId: number) => {
    loading.value++
    const syouninType = OptionConst.SHINSEI_CODE_FOR_FLOW_TYPES.find(v => v.code == shinseiType)?.flow_type ?? OptionConst.SYOUNIN_FLOW_TYPES.DEFAULT
    const res = await syouninSrv.getFlow(id, syouninType, userId)
    loading.value--
    return res
  }

  // 読み込み中か判断する
  const isLoading = computed(() => {
    return !(loading.value === 0 || isError.value)
  })

  return {
    getShinseiInfo,
    isLoading,
    isError,
    getShinseiSyouninFlow,
    getUser,
    getShinseiOptions,
    options,
    flow,
    user,
    setShinseiType,
    getApprover,
  }
}
